/*
  bfroz-motion.js : port vanilla de _source/src/components/scroll/Reveal.tsx (Reveal, Stagger,
  StaggerItem) et Parallax.tsx (framer-motion), mêmes maths, sans dépendance.

  API (attributs HTML, voir theme/CONVENTIONS.md) :
    data-reveal="up|down|left|right|none"   data-reveal-delay="0.1"   data-reveal-distance="56"
      Révélation LIÉE au défilement (dans les deux sens) : p = position du haut de l'élément
      entre 95 % et 35 % de la hauteur de fenêtre (0 → 1). start = min(0.6, max(0, delay)).
      opacité 0→1 sur [start, start+0.35] ; x/y ±distance→0 et scale 0.97→1 sur [start, start+0.4].
    data-stagger  data-stagger-gap="0.12"  data-stagger-delay="0"  data-stagger-amount="0.2"  data-stagger-once="true"
      + enfants data-stagger-item="up|left|right|down|none"  data-stagger-distance="48"
      IntersectionObserver (seuil = amount) ; chaque enfant passe de (opacité 0, décalage, scale 0.97)
      à l'état final en 1.1 s cubic-bezier(0.16, 1, 0.3, 1), délai = delay + index × gap.
    data-parallax="140"
      Progression sur [l'élément entre par le bas, il sort par le haut] → translateY de +offset à −offset,
      appliqué au PREMIER ENFANT (ou à l'élément lui-même s'il n'a pas d'enfant élément).
    prefers-reduced-motion: reduce → aucun mouvement, tout visible.

  window.BFROZ.motion.refresh() : à appeler après avoir injecté du contenu animé dans le DOM.
  window.BFROZ.motionReady = true une fois initialisé (le layout retire .bfroz-js après 3 s sinon).
*/
(function () {
  'use strict';

  var BFROZ = (window.BFROZ = window.BFROZ || {});
  var root = document.documentElement;
  var READY = 'bfroz-motion-ready';
  var EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var DURATION = 1.1;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function num(v, d) {
    var n = parseFloat(v);
    return isNaN(n) ? d : n;
  }
  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }
  // Équivalent de useTransform(value, [a, b], [from, to]) avec clamp (défaut framer).
  function map(p, a, b, from, to) {
    if (b <= a) return p >= b ? to : from;
    return from + (to - from) * clamp01((p - a) / (b - a));
  }
  function offsetFor(dir, dist) {
    switch (dir) {
      case 'down':
        return { x: 0, y: -dist };
      case 'left':
        return { x: dist, y: 0 };
      case 'right':
        return { x: -dist, y: 0 };
      case 'none':
        return { x: 0, y: 0 };
      default:
        return { x: 0, y: dist };
    }
  }
  // Position « layout » (offsetTop cumulé), insensible aux transforms : c'est ce que mesure framer.
  function layoutTop(el) {
    var y = 0;
    var n = el;
    while (n) {
      y += n.offsetTop;
      n = n.offsetParent;
    }
    return y;
  }
  function transformString(x, y, s) {
    if (Math.abs(x) < 0.01 && Math.abs(y) < 0.01 && Math.abs(1 - s) < 0.0005) return 'none';
    return 'translate3d(' + x.toFixed(2) + 'px, ' + y.toFixed(2) + 'px, 0) scale(' + s.toFixed(4) + ')';
  }

  var reveals = [];
  var parallaxes = [];
  var layoutDirty = true;
  var ticking = false;
  var frame = 0;

  // ---------- reduced motion : tout visible, rien ne bouge ----------
  if (reduce) {
    var all = document.querySelectorAll('[data-reveal], [data-stagger-item]');
    for (var i = 0; i < all.length; i++) all[i].classList.add(READY);
    BFROZ.motion = { refresh: function () {
      var more = document.querySelectorAll('[data-reveal], [data-stagger-item]');
      for (var j = 0; j < more.length; j++) more[j].classList.add(READY);
    } };
    BFROZ.motionReady = true;
    return;
  }

  // ---------- Reveal ----------
  function registerReveal(el) {
    if (el.__bfrozReveal) return;
    var dir = el.getAttribute('data-reveal') || 'up';
    var dist = num(el.getAttribute('data-reveal-distance'), 56);
    var delay = num(el.getAttribute('data-reveal-delay'), 0);
    var off = offsetFor(dir, dist);
    var r = { el: el, x0: off.x, y0: off.y, start: Math.min(0.6, Math.max(0, delay)), top: 0, active: false };
    el.__bfrozReveal = r;
    reveals.push(r);
    el.classList.add(READY);
  }

  // ---------- Parallax ----------
  function registerParallax(el) {
    if (el.__bfrozParallax) return;
    var offset = num(el.getAttribute('data-parallax'), 140);
    var target = el.firstElementChild || el;
    target.style.willChange = 'transform';
    var p = { el: el, target: target, offset: offset, top: 0, height: 0 };
    el.__bfrozParallax = p;
    parallaxes.push(p);
  }

  // ---------- Stagger ----------
  var io = null;
  function ioCallback(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var st = entry.target.__bfrozStagger;
      if (!st) continue;
      if (entry.isIntersecting) {
        showStagger(st);
        if (st.once && io) io.unobserve(entry.target);
      } else if (!st.once && st.shown) {
        hideStagger(st);
      }
    }
  }
  function staggerItems(container) {
    var nodes = container.querySelectorAll('[data-stagger-item]');
    var items = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // un item appartient au conteneur data-stagger le plus proche
      if (el.parentElement && el.parentElement.closest('[data-stagger]') !== container) continue;
      var dir = el.getAttribute('data-stagger-item') || 'up';
      var dist = num(el.getAttribute('data-stagger-distance'), 48);
      var off = offsetFor(dir, dist);
      items.push({ el: el, x: off.x, y: off.y });
    }
    return items;
  }
  function hideStagger(st) {
    st.shown = false;
    for (var i = 0; i < st.items.length; i++) {
      var it = st.items[i];
      it.el.style.transition = 'none';
      it.el.style.opacity = '0';
      it.el.style.transform = transformString(it.x, it.y, 0.97);
      it.el.classList.add(READY);
    }
  }
  function showStagger(st) {
    st.shown = true;
    for (var i = 0; i < st.items.length; i++) {
      (function (it, index) {
        var d = (st.delay + index * st.gap).toFixed(3) + 's';
        // forcer la prise en compte de l'état caché avant la transition
        void it.el.offsetWidth;
        it.el.style.transition = 'opacity ' + DURATION + 's ' + EASE + ' ' + d + ', transform ' + DURATION + 's ' + EASE + ' ' + d;
        it.el.style.opacity = '1';
        it.el.style.transform = 'none';
      })(st.items[i], i);
    }
  }
  function registerStagger(container) {
    if (container.__bfrozStagger) return;
    var st = {
      el: container,
      gap: num(container.getAttribute('data-stagger-gap'), 0.12),
      delay: num(container.getAttribute('data-stagger-delay'), 0),
      amount: num(container.getAttribute('data-stagger-amount'), 0.2),
      once: container.getAttribute('data-stagger-once') !== 'false',
      items: staggerItems(container),
      shown: false
    };
    container.__bfrozStagger = st;
    hideStagger(st);
    if (!('IntersectionObserver' in window)) {
      showStagger(st);
      return;
    }
    // un observer par seuil (framer : viewport.amount)
    var key = String(st.amount);
    observers[key] = observers[key] || new IntersectionObserver(ioCallback, { threshold: st.amount });
    io = observers[key];
    io.observe(container);
    st.io = io;
  }
  var observers = {};

  // ---------- collecte ----------
  function collect() {
    var i;
    var r = document.querySelectorAll('[data-reveal]');
    for (i = 0; i < r.length; i++) registerReveal(r[i]);
    var p = document.querySelectorAll('[data-parallax]');
    for (i = 0; i < p.length; i++) registerParallax(p[i]);
    var s = document.querySelectorAll('[data-stagger]');
    for (i = 0; i < s.length; i++) registerStagger(s[i]);
    layoutDirty = true;
  }

  function measure() {
    var i;
    for (i = 0; i < reveals.length; i++) reveals[i].top = layoutTop(reveals[i].el);
    for (i = 0; i < parallaxes.length; i++) {
      parallaxes[i].top = layoutTop(parallaxes[i].el);
      parallaxes[i].height = parallaxes[i].el.offsetHeight;
    }
    layoutDirty = false;
  }

  function update() {
    ticking = false;
    frame++;
    if (layoutDirty || frame % 30 === 0) measure();
    var vh = window.innerHeight || 1;
    var sy = window.pageYOffset || root.scrollTop || 0;
    var i;

    for (i = 0; i < reveals.length; i++) {
      var r = reveals[i];
      var top = r.top - sy;
      // offset: ["start 95%", "start 35%"]
      var p = clamp01((0.95 * vh - top) / (0.6 * vh));
      var e1 = Math.min(1, r.start + 0.35);
      var e2 = Math.min(1, r.start + 0.4);
      var o = map(p, r.start, e1, 0, 1);
      var x = map(p, r.start, e2, r.x0, 0);
      var y = map(p, r.start, e2, r.y0, 0);
      var s = map(p, r.start, e2, 0.97, 1);
      var st = r.el.style;
      st.opacity = o.toFixed(4);
      st.transform = transformString(x, y, s);
      var active = p > 0 && p < 1;
      if (active !== r.active) {
        r.active = active;
        st.willChange = active ? 'transform, opacity' : '';
      }
    }

    for (i = 0; i < parallaxes.length; i++) {
      var px = parallaxes[i];
      var t = px.top - sy;
      // offset: ["start end", "end start"]
      var pp = clamp01((vh - t) / (vh + px.height || 1));
      var yy = px.offset - 2 * px.offset * pp;
      px.target.style.transform = 'translate3d(0, ' + yy.toFixed(2) + 'px, 0)';
    }
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }
  function invalidate() {
    layoutDirty = true;
    requestUpdate();
  }

  collect();
  update();

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', invalidate, { passive: true });
  window.addEventListener('orientationchange', invalidate);
  window.addEventListener('load', invalidate);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(invalidate);
  if ('ResizeObserver' in window) {
    var roTimer = null;
    new ResizeObserver(function () {
      if (roTimer) return;
      roTimer = setTimeout(function () {
        roTimer = null;
        invalidate();
      }, 60);
    }).observe(document.body);
  }

  BFROZ.motion = {
    refresh: function () {
      collect();
      invalidate();
    },
    update: requestUpdate
  };
  BFROZ.motionReady = true;
})();
